import axios from "axios"
import router from "@/router"
import store from "@/store/store"

// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: process.env.VUE_APP_REQUESTS_TIMEOUT,
})

// Access Token Interceptor
service.interceptors.request.use(
  function (config) {
    const accessToken = store.getters.accessToken
    if (accessToken) {
      config.headers.Authorization = "Bearer " + accessToken
    }
    config.withCredentials = true
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

// Token Refresh Interceptor
let isAlreadyFetchingAccessToken = false
let subscribers = []

function onAccessTokenFetched(access_token) {
  subscribers = subscribers.filter((callback) => callback(access_token))
}

function addSubscriber(callback) {
  subscribers.push(callback)
}

// Decodes a JWT's payload without verifying it -- used only to check
// whether the token we already hold is still within its own expiry, not
// as an authorization check (the server independently verifies every
// request). Returns true if the token is missing, malformed, or expired.
function isAccessTokenExpired(token) {
  if (!token) return true
  try {
    // JWTs are base64url-encoded (RFC 7515), which can contain "-"/"_"
    // that atob() doesn't understand -- convert to standard base64 first.
    const unpadded = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")
    const base64 = unpadded.padEnd(
      unpadded.length + ((4 - (unpadded.length % 4)) % 4),
      "="
    )
    const payload = JSON.parse(atob(base64))
    if (!payload.exp) return true
    return Date.now() >= payload.exp * 1000
  } catch (error) {
    return true
  }
}

service.interceptors.response.use(
  function (response) {
    return response
  },
  function (error) {
    const { config: originalRequest, response } = error
    const status = response?.status
    // Never log the error object or the token -- the request config carries
    // the Authorization header (GFB-1408).
    console.error(
      "API request failed:",
      status ?? "no response",
      originalRequest?.method?.toUpperCase(),
      originalRequest?.url
    )
    if (!originalRequest) {
      return Promise.reject(error)
    }
    const isAuthError = status === 401 || status === 403
    if (isAuthError || response?.data?.error) {
      if (
        originalRequest.url.includes("/api/auth/login") ||
        originalRequest.url.includes("/api/auth/resend-claim")
      ) {
        store.commit("CLEAR_USER_INFO")
        store.commit("CLEAR_USER_PROFILE")
        router.push("/login")
        return Promise.reject(error)
      } else if (originalRequest.url.includes("/api/auth/refresh-token")) {
        // A refresh attempt can fail for reasons that don't mean the
        // session is actually dead -- e.g. a browser (Safari's ITP is a
        // known case) simply failing to persist/send the refresh-token
        // cookie, even though the access token we already hold is still
        // valid. Only treat this as a real logout if that token has
        // actually expired; otherwise let the caller fail this one
        // request and keep using the still-valid token.
        if (isAccessTokenExpired(store.getters.accessToken)) {
          store.commit("CLEAR_USER_INFO")
          store.commit("CLEAR_USER_PROFILE")
          router.push("/login")
        }
        return Promise.reject(error)
      } else {
        // Refresh-and-retry only for auth failures, and at most once per
        // request (GFB-1408). 403 still counts until every API returns 401
        // for expired tokens, but a request that fails again after a
        // refresh is rejected instead of triggering another refresh.
        if (!isAuthError || originalRequest._retry) {
          return Promise.reject(error)
        }
        originalRequest._retry = true

        if (!isAlreadyFetchingAccessToken) {
          isAlreadyFetchingAccessToken = true
          store
            .dispatch("auth/refreshToken")
            .then(({ data }) => {
              const { accessToken } = data
              isAlreadyFetchingAccessToken = false
              onAccessTokenFetched(accessToken)
            })
            .catch(() => {
              isAlreadyFetchingAccessToken = false
              // Notify every request queued behind this refresh so their
              // promises reject instead of hanging forever.
              onAccessTokenFetched(null)
            })
        }

        const retryOriginalRequest = new Promise((resolve, reject) => {
          addSubscriber((access_token) => {
            if (!access_token) {
              reject(error)
              return
            }
            originalRequest.headers.Authorization = `Bearer ${access_token}`
            resolve(axios(originalRequest))
          })
        })
        return retryOriginalRequest
      }
    }
    return Promise.reject(error)
  }
)

export default service
